import { PopoverContent, PopoverPortal, PopoverRoot, PopoverTrigger } from "reka-ui";
import { computed, defineComponent, ref } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";

type Side = "top" | "bottom" | "left" | "right";

const Root = defineComponent({
  name: "Popover",
  emits: {
    "update:open": (_open: boolean) => true,
  },
  props: {
    defaultOpen: p.boolean(false),
    open: p.boolean(null),
    modal: p.boolean(false),
  },
  setup(props, { emit, slots }) {
    const _open = ref(false);

    const open = computed({
      get: () => (props.open !== null ? props.open : _open.value),
      set: (val: boolean) => {
        if (props.open === null) {
          _open.value = val;
        }
        emit("update:open", val);
      },
    });

    return () => (
      <PopoverRoot defaultOpen={props.defaultOpen} v-model:open={open.value} modal={props.modal}>
        {slots.default?.()}
      </PopoverRoot>
    );
  },
});

const Content = defineComponent({
  name: "PopperContent",
  emits: {
    openAutoFocus: (_ev: Event) => true,
    closeAutoFocus: (_ev: Event) => true,
    focusOutside: (_ev: Event) => true,
    interactOutside: (_ev: Event) => true,
    pointerDownOutside: (_ev: Event) => true,
  },
  props: {
    forceMount: p.boolean(false),
    sideOffset: p.number(4),
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
    disableOutsidePointerEvents: p.boolean(false),
    sticky: p.string<"partial" | "always">("partial"),
    side: p.string<Side>("bottom"),
    align: p.string<"end" | "center" | "start">("end"),
    alignOffset: p.number(0),
    avoidCollisions: p.boolean(true),
    collisionBoundary: p.custom<Element | (Element | null)[] | null>([]),
    collisionPadding: p.number(4),
    arrowPadding: p.number(4),
    hideWhenDetached: p.boolean(false),
    updatePositionStrategy: p.string<"always" | "optimized">("optimized"),
    prioritizePosition: p.boolean(false),
    fullWidth: p.boolean(false),
    highZIndex: p.boolean(false),
  },
  setup(props, { slots, emit, attrs }) {
    return () => (
      <PopoverPortal>
        <PopoverContent
          forceMount={props.forceMount}
          sideOffset={props.sideOffset}
          onCloseAutoFocus={(val: Event) => emit("closeAutoFocus", val)}
          onOpenAutoFocus={(val: Event) => emit("openAutoFocus", val)}
          onFocusOutside={(val: Event) => emit("focusOutside", val)}
          onInteractOutside={(val: Event) => emit("interactOutside", val)}
          onPointerDownOutside={(val: Event) => emit("pointerDownOutside", val)}
          align={props.align}
          alignOffset={props.alignOffset}
          avoidCollisions={props.avoidCollisions}
          collisionBoundary={props.collisionBoundary}
          collisionPadding={props.collisionPadding}
          arrowPadding={props.arrowPadding}
          hideWhenDetached={props.hideWhenDetached}
          updatePositionStrategy={props.updatePositionStrategy}
          prioritizePosition={props.prioritizePosition}
          asChild={props.asChild}
          as={props.as}
          disableOutsidePointerEvents={props.disableOutsidePointerEvents}
          sticky={props.sticky}
          side={props.side}
          class={cn(
            "dark:bg-gray-950 z-80 rounded-md border bg-popover p-4 text-foreground shadow-md outline-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            props.fullWidth && "w-(--reka-popover-trigger-width)",
            attrs.class ?? ""
          )}
        >
          {slots.default?.()}
        </PopoverContent>
      </PopoverPortal>
    );
  },
});

const Trigger = defineComponent({
  name: "PopoverTrigger",
  props: {
    asChild: p.boolean(false),
    as: p.string<keyof HTMLElementTagNameMap>(),
  },
  setup(props, { slots }) {
    return () => <PopoverTrigger {...props}>{slots.default?.()}</PopoverTrigger>;
  },
});

export default {
  Root,
  Content,
  Trigger,
};
